// modules/ad-assets/PostsTab.tsx
// Каскадний фільтр: сторінка → 2 останніх пости

import React, { useEffect, useState } from 'react';
import { useAssets } from './useAssets.ts';
import type { Page, Post } from '../../core/types.ts';

interface Props {
  socialAccountId: string;
  accountName?: string | null;
}

const POST_TYPE_LABEL: Record<string, string> = {
  text: 'Текст',
  photo: 'Фото',
  video: 'Відео',
  link: 'Посилання',
  reel: 'Reel',
};

const POST_TYPE_BADGE: Record<string, string> = {
  text: 'badge--active',
  photo: 'badge--currency',
  video: 'badge--expired',
  link: 'badge--active',
  reel: 'badge--disconnected',
};

function truncate(s: string | null, len = 120): string {
  if (!s) return '';
  return s.length > len ? s.slice(0, len) + '…' : s;
}

export function PostsTab({ socialAccountId, accountName }: Props) {
  const {
    pages, pagesLoading, fetchPages,
    posts, postsLoading, postsError, fetchPosts,
  } = useAssets();

  const [selectedPageId, setSelectedPageId] = useState<string>('');

  // Завантажити сторінки коли змінюється акаунт
  useEffect(() => {
    if (socialAccountId) {
      fetchPages(socialAccountId);
      setSelectedPageId('');
    }
  }, [socialAccountId, fetchPages]);

  // Коли список сторінок завантажився — вибрати першу
  useEffect(() => {
    if (pages.length > 0 && !selectedPageId) {
      setSelectedPageId(pages[0].id);
    }
  }, [pages, selectedPageId]);

  // Завантажити пости при зміні вибраної сторінки
  useEffect(() => {
    if (selectedPageId) {
      fetchPosts(selectedPageId);
    }
  }, [selectedPageId, fetchPosts]);

  const selectedPage = pages.find((p) => p.id === selectedPageId);

  return (
    <div className="page-body" style={{ padding: 0 }}>
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h2 style={{ fontSize: '1rem', marginBottom: 'var(--space-1)' }}>Пости</h2>
        {accountName && (
          <p className="text-muted text-sm">Акаунт: <span className="text-subtle">{accountName}</span></p>
        )}
      </div>

      {/* Page filter */}
      <div className="glass-card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
        <label
          htmlFor="page-select"
          className="text-sm text-muted"
          style={{ display: 'block', marginBottom: 'var(--space-2)' }}
        >
          Оберіть сторінку
        </label>
        {pagesLoading ? (
          <div className="skeleton" style={{ height: 40, borderRadius: 'var(--radius-sm)' }} />
        ) : pages.length === 0 ? (
          <p className="text-sm text-subtle">Немає сторінок — спочатку синхронізуй акаунт</p>
        ) : (
          <select
            id="page-select"
            className="filter-select"
            value={selectedPageId}
            onChange={(e) => setSelectedPageId(e.target.value)}
          >
            {pages.map((p: Page) => (
              <option key={p.id} value={p.id}>
                {p.name ?? p.id}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Posts */}
      {selectedPageId && (
        <>
          {postsLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {[1, 2].map((i) => (
                <div key={i} className="skeleton" style={{ height: 120, borderRadius: 'var(--radius-md)' }} />
              ))}
            </div>
          )}

          {postsError && !postsLoading && (
            <div className="notification notification--error">{postsError}</div>
          )}

          {!postsLoading && !postsError && posts.length === 0 && (
            <div className="glass-card empty-state">
              <div className="empty-state__title">Немає постів</div>
              <div className="empty-state__desc">
                На цій сторінці не знайдено опублікованих постів або не завершено синхронізацію.
              </div>
            </div>
          )}

          {!postsLoading && posts.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <p className="text-xs text-subtle">
                Показано {posts.length} {posts.length === 1 ? 'пост' : 'пости'} •{' '}
                {selectedPage?.name ?? selectedPageId}
              </p>
              {posts.map((post: Post) => {
                const date = post.createdTime
                  ? new Intl.DateTimeFormat('uk-UA', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }).format(new Date(post.createdTime))
                  : null;

                return (
                  <div key={post.id} className="glass-card" style={{ padding: 'var(--space-5)' }}>
                    {/* Header */}
                    <div className="flex items-center gap-2" style={{ marginBottom: 'var(--space-3)' }}>
                      <span className={`badge ${POST_TYPE_BADGE[post.postType] ?? 'badge--active'}`}>
                        {POST_TYPE_LABEL[post.postType] ?? post.postType}
                      </span>
                      {date && <span className="text-xs text-subtle">{date}</span>}
                    </div>

                    {/* Content row */}
                    <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
                      {/* Thumbnail */}
                      {(post.fullPicture || post.pictureUrl) && (
                        <div
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: 'var(--radius-md)',
                            overflow: 'hidden',
                            flexShrink: 0,
                            background: 'var(--color-surface-2)',
                          }}
                        >
                          <img
                            src={post.fullPicture ?? post.pictureUrl ?? ''}
                            alt="post"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).parentElement!.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      {/* Text */}
                      <div style={{ flex: 1 }}>
                        {post.message ? (
                          <p className="text-sm" style={{ marginBottom: 'var(--space-2)' }}>
                            {truncate(post.message)}
                          </p>
                        ) : (
                          <p className="text-sm text-subtle" style={{ marginBottom: 'var(--space-2)' }}>
                            [без тексту]
                          </p>
                        )}

                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs text-subtle">{post.id}</span>
                          {post.permalinkUrl && (
                            <a
                              href={post.permalinkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs"
                              style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                            >
                              Відкрити
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
